// Baselayer Results Explorer — Officers tab. Table of officers: name, titles
// (joined or "—"), SoS state chips, source chips.

import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyState,
} from "@/design-system/components/ui";
import { Chips } from "../tabHelpers";
import {
  asArray,
  asObject,
  asString,
  pick,
  type ParsedKyb,
} from "../parse";

export function OfficersTab({ kyb }: { kyb: ParsedKyb }) {
  const b = kyb.business;
  const raw = asArray(
    pick(b, "officers", "people") ?? pick(kyb.raw, "officers"),
  );

  const officers = raw.map((o) => {
    const obj = asObject(o);
    const titles = asArray(pick(obj, "titles", "roles"))
      .map((t) => asString(t) ?? asString(pick(asObject(t), "title", "name")))
      .filter((t): t is string => !!t);
    return {
      name:
        asString(pick(obj, "name", "full_name")) ??
        ([asString(pick(obj, "first_name")), asString(pick(obj, "last_name"))]
          .filter(Boolean)
          .join(" ") ||
          "—"),
      titles,
      sos: asArray(pick(obj, "sos_states", "states")).map(
        (s) => asString(s) ?? asString(pick(asObject(s), "state")),
      ),
      sources: asArray(pick(obj, "sources")).map(
        (s) => asString(s) ?? asString(pick(asObject(s), "name", "source")),
      ),
    };
  });

  if (officers.length === 0) {
    return (
      <EmptyState
        title="No officers"
        description="No officers were returned for this subject."
      />
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Name</TH>
          <TH>Titles</TH>
          <TH>SoS</TH>
          <TH>Sources</TH>
        </TR>
      </THead>
      <TBody>
        {officers.map((o, i) => (
          <TR key={`${o.name}-${i}`}>
            <TD>{o.name}</TD>
            <TD>{o.titles.length ? o.titles.join(", ") : "—"}</TD>
            <TD>
              <Chips values={o.sos} />
            </TD>
            <TD>
              <Chips values={o.sources} />
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
